import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";

import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import ReactQuill from "react-quill";
import Select, { createFilter } from "react-select";
import { helperServices } from "../../shared/helper";
import "react-quill/dist/quill.snow.css";
import "./AddCaseStudy.css";

import { doctorActions } from "../../core/actions";
import { PROCEDURE_CONSTANTS_DETAILS } from "../../shared/constants";
import { parseCaseStudyData } from "../../shared/helper/caseStudyUtils";

const {
  PROCEDURE_GROUPNAME_PATIENT_CASE_STUDY,
  PROCEDURE_CLASS_PATIENT_CASE_STUDY_DATE,
  PROCEDURE_CLASS_PATIENT_CASE_STUDY_DOCTOR_NOTES,
  PROCEDURE_SUBGROUP_PATIENT_CASE_STUDY_PREFIX,
} = PROCEDURE_CONSTANTS_DETAILS;

const CASE_STUDY_EDITOR_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ align: [] }],
    ["blockquote"],
    ["clean"],
  ],
};

const CASE_STUDY_EDITOR_FORMATS = [
  "header",
  "bold",
  "italic",
  "underline",
  "list",
  "bullet",
  "align",
  "blockquote",
];

const getPlainTextFromHtml = (html = "") => {
  const container = document.createElement("div");
  container.innerHTML = html;

  return (container.textContent || container.innerText || "")
    .replace(/\u00a0/g, " ")
    .trim();
};
const getPlainTextFromContent = (content) => {
  if (typeof content === "string") {
    return getPlainTextFromHtml(content);
  }

  if (!Array.isArray(content?.ops)) {
    return "";
  }

  return content.ops
    .map((operation) =>
      typeof operation.insert === "string"
        ? operation.insert
        : " ",
    )
    .join("")
    .replace(/\u00a0/g, " ")
    .trim();
};

function AddCaseStudy(props) {
  const dispatch = useDispatch();
  const [doctorOptions, setDoctorOptions] = useState([]);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const caseStudyEditorRef = useRef(null);
  const isReadOnly = Boolean(props.readOnly);

  /*
   * Get the doctor who is currently attending the patient.
   */
  const attendingDoctor =
    props.attendingDoctor ||
    props.patientData?.person ||
    props.patientData?.doctor ||
    {};

  const savedDoctorId =
    props.mode === "edit"
      ? props.caseStudy?.doctorId ||
        attendingDoctor?.personId
      : attendingDoctor?.personId;

  const createDoctorOption = (doctor) => {
    const fullName = helperServices.getFullName(
      doctor.first_name || "",
      null,
      doctor.last_name || "",
    );

    const label =
      `${doctor.displaytitle || ""} ${fullName}` +
      `${doctor.department ? `, Dep of ${doctor.department}` : ""}` +
      `${doctor.qualification ? ` (${doctor.qualification})` : ""}`;

    return {
      value: doctor.personId,
      label: label.trim(),
      doctor,
    };
  };

  useEffect(() => {
    dispatch(
      doctorActions.getDoctorList({}, (response) => {
        const options = (response || [])
          .filter(
            (doctor) =>
              doctor?.type?.toLowerCase() === "doctor",
          )
          .map(createDoctorOption);

        setDoctorOptions(options);

        const defaultDoctor = options.find(
          (option) =>
            String(option.value) === String(savedDoctorId),
        );

        if (defaultDoctor) {
          setSelectedDoctor(defaultDoctor);
        }
      }),
    );
  }, [dispatch, savedDoctorId]);

  /*
   * Return today's local date in yyyy-MM-dd format.
   */
  const getCurrentDate = () => {
    const today = new Date();
    const timezoneOffset = today.getTimezoneOffset() * 60000;

    return new Date(today.getTime() - timezoneOffset)
      .toISOString()
      .split("T")[0];
  };

  /*
   * Read the existing Case Study when editing.
   */
  const getExistingCaseStudy = () => {
    if (props.mode !== "edit") {
      return {};
    }

    if (
      props.caseStudy?.dateRow ||
      props.caseStudy?.notesRow ||
      props.caseStudy?.legacyRow
    ) {
      return {
        date:
          props.caseStudy.date ||
          props.caseStudy.dateRow?.notes ||
          "",
        notes:
          props.caseStudy.notes ||
          props.caseStudy.notesRow?.notes ||
          props.caseStudy.legacyRow?.notes ||
          "",
      };
    }

    if (!props.caseStudy?.notes) {
      return {};
    }

    return parseCaseStudyData(
      props.caseStudy.notes,
      props.caseStudy.longdesc,
    );
  };

  const existingCaseStudy = getExistingCaseStudy();

  /*
   * Form state.
   */
  const [caseStudyDate, setCaseStudyDate] = useState(
    existingCaseStudy.date || getCurrentDate(),
  );

  const [caseStudyNotes, setCaseStudyNotes] = useState(
    existingCaseStudy.notes || props.caseStudy?.longdesc || {
      ops: [{ insert: "\n" }],
    },
  );
  const [isSaving, setIsSaving] = useState(false);

  /*
   * Form validation messages.
   */
  const [formErrors, setFormErrors] = useState({
    caseStudyDate: "",
    caseStudyNotes: "",
  });

  /*
   * Called when the Case Study date changes.
   */
  const handleDateChange = (event) => {
    const value = event.target.value;

    setCaseStudyDate(value);

    setFormErrors((previousErrors) => ({
      ...previousErrors,
      caseStudyDate: value ? "" : "Please select a date",
    }));
  };

  /*
   * Called when Case Study notes change.
   */
  const handleNotesChange = (
    html,
    _delta,
    _source,
    editor,
  ) => {
    setCaseStudyNotes(html);

    setFormErrors((previousErrors) => ({
      ...previousErrors,
      caseStudyNotes: editor.getText().trim()
        ? ""
        : "Please enter patient case study notes",
    }));
  };
  /*
   * Validate the Date and Notes fields.
   */
  const validateForm = () => {
    const errors = {
      caseStudyDate: caseStudyDate
        ? ""
        : "Please select a date",

      caseStudyNotes: getPlainTextFromContent(caseStudyNotes)
        ? ""
        : "Please enter patient case study notes",
    };

    setFormErrors(errors);

    return !Object.values(errors).some(
      (errorMessage) => errorMessage !== "",
    );
  };

  /*
   * Save the Patient Case Study date and notes as separate rows.
   *
   * API flow:
   * doctorActions.addCaseRecord()
   * -> doctorServices.addCaseRecord()
   * -> POST /v1/caserecord
   */

  // Saves one Case Study as paired Date and Doctor Notes procedure rows.
  const submit = (event) => {
    event.preventDefault();

    if (isReadOnly) {
      return;
    }

    if (!validateForm()) {
      return;
    }

    const targetPatientId =
      props.mode === "edit"
        ? props.caseStudy?.patientId
        : props.patientData?.id;

    const targetVisitId =
      props.mode === "edit"
        ? props.caseStudy?.visitId
        : props.patientData?.visit?.id;

    /*
     * Confirm that patient and visit details exist.
     */
    if (!targetPatientId || !targetVisitId) {
      props.onModalClose({
        error: "Patient or visit information is missing.",
      });
      return;
    }

    /*
     * Confirm that the attending doctor exists.
     */
    if (!selectedDoctor?.value) {
      props.onModalClose({
        error: "Attending doctor information is missing.",
      });
      return;
    }

    const subgroup =
      props.caseStudy?.subgroup ||
      `${PROCEDURE_SUBGROUP_PATIENT_CASE_STUDY_PREFIX}${Date.now()}`;

    const commonDetails = {
      patientId: targetPatientId,
      visitId: targetVisitId,
      preparedBy: null,
      doctorId: selectedDoctor.value,
      groupname: PROCEDURE_GROUPNAME_PATIENT_CASE_STUDY,
      subgroup,
      longdesc: null,
      markForDel: false,
      billable: false,
    };

    const caseStudyRows = [
      {
        ...commonDetails,
        ...(props.caseStudy?.dateRow?.id
          ? { id: props.caseStudy.dateRow.id }
          : {}),
        // Preserve an existing legacy class while new records use "Date".
        claSS:
          props.caseStudy?.dateRow?.claSS ||
          PROCEDURE_CLASS_PATIENT_CASE_STUDY_DATE,
        notes: caseStudyDate,
      },
      {
        ...commonDetails,
        ...(props.caseStudy?.notesRow?.id
          ? { id: props.caseStudy.notesRow.id }
          : {}),
        // Preserve an existing legacy class while new records use "Doctor Notes".
        claSS:
          props.caseStudy?.notesRow?.claSS ||
          PROCEDURE_CLASS_PATIENT_CASE_STUDY_DOCTOR_NOTES,
        notes: caseStudyNotes,
      },
    ];

    setIsSaving(true);

    dispatch(
      doctorActions.addCaseRecord(
        caseStudyRows,
        (response) => {
          setIsSaving(false);

          if (response?.successMessages) {
            props.onModalClose({
              success: "Patient case study notes saved successfully.",
            });
          } else {
            props.onModalClose({
              error: "Failed to save patient case study notes.",
            });
          }
        },
      ),
    );
  };

  /*
   * Close the Case Study page without saving.
   */
  const closeModal = () => {
    props.onModalClose();
  };

  return (
    <div className="bg-none nav-wrapper h-100 d-flex flex-column conultant-view">
      {/* Close button */}
      <IconButton
        aria-label="Close Case Study"
        onClick={closeModal}
        sx={{
          position: "absolute",
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <CloseIcon />
      </IconButton>

      <form
        className="d-flex flex-column p-4"
        onSubmit={submit}
      >
        <div className="case-study-form__top-row">
          {/* Attending Doctor field */}
          <div className="case-study-form__doctor-field">
            <label className="service-lable">
              Attending Doctor:
            </label>

            <Select
              className="select-tag"
              name="Attending Doctor"
              value={selectedDoctor}
              options={doctorOptions}
              onChange={setSelectedDoctor}
              filterOption={createFilter(doctorOptions)}
              placeholder="Select attending doctor"
              isSearchable
              isClearable
              isDisabled={isReadOnly}
            />
          </div>

          {/* Case Study Date */}
          <div className="case-study-form__date-field">
            <label className="service-lable">
              Date:
            </label>
            <TextField
              type="date"
              value={caseStudyDate}
              onChange={handleDateChange}
              InputProps={{
                readOnly: isReadOnly,
              }}
              InputLabelProps={{
                shrink: true,
              }}
              fullWidth
            />

            {formErrors.caseStudyDate && (
              <div style={{ color: "#EC4147" }}>
                {formErrors.caseStudyDate}
              </div>
            )}
          </div>
        </div>

        {/* Patient Case Study Notes */}
        <div className="my-3">
          <label className="service-lable">
            Patient Case Study Notes:
          </label>

          <ReactQuill
            ref={caseStudyEditorRef}
            className="case-study-editor"
            theme="snow"
            value={caseStudyNotes}
            onChange={handleNotesChange}
            readOnly={isReadOnly}
            modules={
              isReadOnly
                ? { toolbar: false }
                : CASE_STUDY_EDITOR_MODULES
            }
            formats={CASE_STUDY_EDITOR_FORMATS}
            placeholder="Type patient case study notes here..."
          />

          {formErrors.caseStudyNotes && (
            <div style={{ color: "#EC4147" }}>
              {formErrors.caseStudyNotes}
            </div>
          )}
        </div>

        {/* Save button */}
        {!isReadOnly && (
          <div className="d-flex justify-content-end my-4">
            <Button
              color="primary"
              variant="contained"
              sx={{ textTransform: "none" }}
              type="submit"
              disabled={isSaving}
            >
              {isSaving ? "Saving..." : "Save"}
            </Button>
          </div>
        )}
      </form>
    </div>
  );
}

export default AddCaseStudy;
